import type { FetchError } from 'ofetch'
import type { ApiErrorType } from '@/uTypes/api'
import { TOKEN_KEY } from '@/constants/login'
import { useAuthStore } from '@/stores/auth'
import { useApiFlagsStore } from '@/stores/apiFlags'

export const useApiUtils = () => {
  const nuxtApp = useNuxtApp()
  // logoutInProgress: 여러 API 요청에서 401이 발생할 경우, 중복 로그아웃 및 리디렉션을 방지하기 위한 전역 플래그
  // isAlertShown: 오류 알림 표시 상태를 관리하는 전역 플래그
  const { isLogoutInProgress, isAlertShown } = storeToRefs(useApiFlagsStore())

  const isFetchError = (err: unknown): err is FetchError => {
    return typeof err === 'object' && err !== null && 'name' in err && 'response' in err
  }

  const getErrorMsgByStatusCode = (
    statusCode: number | null,
    err: FetchError | ApiErrorType
  ): string => {
    // 상태 코드가 없는 경우 (타임아웃 또는 네트워크 오류)
    if (!statusCode) {
      const isTimeout = isFetchError(err) && err.message.toLowerCase().includes('timeout')
      return isTimeout
        ? '요청 시간이 초과되었습니다. 다시 시도해주세요.'
        : '네트워크 연결을 확인해주세요.'
    }

    // HTTP 상태 코드에 따른 메시지
    switch (statusCode) {
      case 403: // Forbidden
        return '해당 작업에 대한 권한이 없습니다.'

      case 404: // Not Found
        return '요청하신 정보를 찾을 수 없습니다.'

      case 500: // Internal Server Error
      case 502: // Bad Gateway
      case 503: // Service Unavailable
      case 504: // Gateway Timeout
        return '서버 오류가 발생했습니다. 잠시 후 다시 시도해주세요.'

      default:
        if (isFetchError(err)) {
          // csr
          return err?.data?.error?.message || '다시 시도해주세요.'
        } else {
          // ssr
          return err?.message || '다시 시도해주세요.'
        }
    }
  }

  const getToken = () => {
    const token = useCookie(TOKEN_KEY)
    return token.value || ''
  }

  // 쿠키 제거 + 로그인 페이지 복귀는 authStore 가 담당한다 (중복 방지 플래그도 그쪽에서 처리)
  const handleLogoutIfUnauthorized = async () => {
    if (!import.meta.client) return

    const authStore = useAuthStore()
    authStore.handleUnauthorized()
  }

  const handleErrorCommon = async (statusCode: number | null, err: FetchError | ApiErrorType) => {
    if (import.meta.server) return

    // 중복 처리 방지
    if (isLogoutInProgress.value) return

    // 401 에러는 알럿 노출 후 로그인 페이지로 이동해야하기 때문에 따로 처리
    if (statusCode === 401) {
      await handleLogoutIfUnauthorized()
      return
    }

    if (isAlertShown.value) return
    isAlertShown.value = true
    const message = getErrorMsgByStatusCode(statusCode, err)

    // nuxtApp.$swal.fire(message)
    // // 일정 시간 후 플래그 초기화
    setTimeout(() => {
      isAlertShown.value = false
    }, 2000)
  }

  const handleCSRError = (err: FetchError) => {
    const statusCode = err?.response?.status ?? null
    handleErrorCommon(statusCode, err)
  }

  const handleSSRError = (err: ApiErrorType) => {
    const errMsg = err.message
    if (errMsg.startsWith('from server-')) {
      const errorMsg = errMsg.split('-')[1]

      const errorObj = {
        statusCode: err.statusCode,
        message: errorMsg
      }
      handleErrorCommon(err.statusCode, errorObj)
    }
  }

  return {
    getToken,
    getErrorMsgByStatusCode,
    handleSSRError,
    handleCSRError
  }
}
